import {
  SELECTABLE_REPLY_TARGETS,
  replyTargetLabel,
  type MainCommandReplyTarget,
} from './main-command-recipients';
import type { WorkerChatReadiness } from './worker-chat-readiness';

export type MainCommandRecipientGroup = 'management' | 'all_workers' | 'everyone';

export type MainCommandMentionCandidate =
  | {
    readonly key: `group:${MainCommandRecipientGroup}`;
    readonly kind: 'group';
    readonly group: MainCommandRecipientGroup;
    readonly label: string;
    readonly detail: string;
    readonly aliases: readonly string[];
    readonly targets: readonly MainCommandReplyTarget[];
    readonly workerIds: readonly string[];
    readonly available: boolean;
  }
  | {
    readonly key: `management:${MainCommandReplyTarget}`;
    readonly kind: 'management';
    readonly target: MainCommandReplyTarget;
    readonly label: string;
    readonly detail: string;
    readonly aliases: readonly string[];
    readonly targets: readonly MainCommandReplyTarget[];
    readonly workerIds: readonly string[];
    readonly available: boolean;
  }
  | {
    readonly key: `worker:${string}`;
    readonly kind: 'worker';
    readonly workerId: string;
    readonly label: string;
    readonly detail: string;
    readonly aliases: readonly string[];
    readonly targets: readonly MainCommandReplyTarget[];
    readonly workerIds: readonly string[];
    readonly available: boolean;
  };

export interface MainCommandMentionQuery {
  readonly start: number;
  readonly end: number;
  readonly query: string;
}

export interface MainCommandMentionResolution {
  readonly content: string;
  readonly targets: readonly MainCommandReplyTarget[];
  readonly workerIds: readonly string[];
  readonly matched: readonly MainCommandMentionCandidate[];
}

function normalized(value: string): string {
  return value.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

function uniqueAliases(values: readonly string[]): readonly string[] {
  return [...new Set(values.map(normalized).filter(Boolean))];
}

function workerAliases(worker: WorkerChatReadiness): readonly string[] {
  const number = `worker ${worker.workerNumber}`;
  const name = worker.workerName.trim();
  return uniqueAliases([
    number,
    name,
    name === '' ? '' : `${number} ${name}`,
    worker.label,
  ]);
}

/**
 * Recipient suggestions are a projection over the management recipients the owner can
 * address and Worker readiness. Group targets carry the same reply-target/Worker-id shapes
 * the existing Main Command RPC already accepts; there is no mention-routing store.
 */
export function buildMainCommandMentionCandidates(input: {
  readonly workers: readonly WorkerChatReadiness[];
  readonly eligibleWorkerIds: ReadonlySet<string>;
}): readonly MainCommandMentionCandidate[] {
  const eligibleIds = input.workers
    .filter((worker) => input.eligibleWorkerIds.has(worker.workerId))
    .map((worker) => worker.workerId);
  const management = [...SELECTABLE_REPLY_TARGETS];
  const candidates: MainCommandMentionCandidate[] = [
    {
      key: 'group:management', kind: 'group', group: 'management', label: '@management',
      detail: management.map(replyTargetLabel).join(', '), aliases: ['management'],
      targets: management, workerIds: [], available: true,
    },
    {
      key: 'group:all_workers', kind: 'group', group: 'all_workers', label: '@all workers',
      detail: `${eligibleIds.length} currently eligible Worker${eligibleIds.length === 1 ? '' : 's'}`,
      aliases: ['all workers'], targets: [], workerIds: eligibleIds,
      available: eligibleIds.length > 0,
    },
    {
      key: 'group:everyone', kind: 'group', group: 'everyone', label: '@everyone',
      detail: `Management and ${eligibleIds.length} eligible Worker${eligibleIds.length === 1 ? '' : 's'}`,
      aliases: ['everyone', 'all'], targets: management, workerIds: eligibleIds, available: true,
    },
    ...SELECTABLE_REPLY_TARGETS.map((target): MainCommandMentionCandidate => ({
      key: `management:${target}`,
      kind: 'management',
      target,
      label: `@${replyTargetLabel(target).toLowerCase()}`,
      detail: replyTargetLabel(target),
      aliases: [replyTargetLabel(target).toLowerCase()],
      targets: [target],
      workerIds: [],
      available: true,
    })),
  ];

  for (const worker of input.workers) {
    if (!worker.listed) continue;
    candidates.push({
      key: `worker:${worker.workerId}`,
      kind: 'worker',
      workerId: worker.workerId,
      label: `@worker ${worker.workerNumber}${worker.workerName.trim() === '' ? '' : ` · ${worker.workerName.trim()}`}`,
      detail: input.eligibleWorkerIds.has(worker.workerId)
        ? `${worker.nodeName ?? 'Unknown machine'} · ${worker.availability.replaceAll('_', ' ')}`
        : `Unavailable for immediate delivery · ${worker.reasonText}`,
      aliases: workerAliases(worker),
      targets: [],
      workerIds: [worker.workerId],
      available: input.eligibleWorkerIds.has(worker.workerId),
    });
  }
  return candidates;
}

/** The active @ fragment at the caret. Spaces are allowed for "all workers" and names. */
export function mainCommandMentionQuery(content: string, caret: number): MainCommandMentionQuery | null {
  const safeCaret = Math.max(0, Math.min(content.length, caret));
  const before = content.slice(0, safeCaret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  const prefix = before.slice(0, at);
  if (prefix !== '' && !/[\s([{,:]$/.test(prefix)) return null;
  const raw = before.slice(at + 1);
  if (/[\n@,:;!?]/.test(raw)) return null;
  return { start: at, end: safeCaret, query: normalized(raw) };
}

export function mainCommandMentionSuggestions(
  content: string,
  caret: number,
  candidates: readonly MainCommandMentionCandidate[],
  limit = 8,
): readonly MainCommandMentionCandidate[] {
  const query = mainCommandMentionQuery(content, caret);
  if (query === null) return [];
  return candidates
    .filter((candidate) => query.query === '' || candidate.aliases.some((alias) => normalized(alias).includes(query.query)))
    .map((candidate, index) => ({
      candidate,
      index,
      score: candidate.aliases.some((alias) => normalized(alias).startsWith(query.query)) ? 0 : 1,
    }))
    .sort((left, right) => left.score - right.score || left.index - right.index)
    .map(({ candidate }) => candidate)
    .slice(0, Math.max(1, limit));
}

/** Remove the UI mention fragment: its routing meaning is now represented by recipient chips. */
export function removeSelectedMainCommandMention(content: string, caret: number): { readonly content: string; readonly caret: number } {
  const query = mainCommandMentionQuery(content, caret);
  if (query === null) return { content, caret };
  const before = content.slice(0, query.start);
  const after = content.slice(query.end);
  const joiner = before !== '' && after !== '' && !/\s$/.test(before) && !/^\s/.test(after) ? ' ' : '';
  const next = `${before}${joiner}${after}`.replace(/^\s+/, '');
  return { content: next, caret: Math.min(next.length, before.length + joiner.length) };
}

function union<T>(into: Set<T>, values: readonly T[]): void {
  for (const value of values) into.add(value);
}

/**
 * Resolve leading typed mentions before the existing RPC is called. The longest
 * canonical alias wins (`@all workers` before `@all`), and the literal prefix is
 * removed from the persisted message body.
 */
export function resolveMainCommandMentions(input: {
  readonly content: string;
  readonly currentTargets: readonly MainCommandReplyTarget[];
  readonly currentWorkerIds: readonly string[];
  readonly candidates: readonly MainCommandMentionCandidate[];
}): MainCommandMentionResolution {
  let remaining = input.content.trimStart();
  const targets = new Set<MainCommandReplyTarget>();
  const workerIds = new Set<string>();
  const matched: MainCommandMentionCandidate[] = [];
  const aliases = input.candidates.flatMap((candidate) => candidate.aliases.map((alias) => ({
    candidate,
    alias: normalized(alias),
  }))).sort((left, right) => right.alias.length - left.alias.length);

  while (remaining.startsWith('@')) {
    const lower = normalized(remaining.slice(1));
    const found = aliases.find(({ alias }) => {
      if (!lower.startsWith(alias)) return false;
      const rawTail = remaining.slice(1 + alias.length);
      return rawTail === '' || /^[\s,:]/.test(rawTail);
    });
    if (found === undefined) break;
    matched.push(found.candidate);
    union(targets, found.candidate.targets);
    union(workerIds, found.candidate.workerIds);
    remaining = remaining.slice(1 + found.alias.length).replace(/^[\s,:]+/, '');
  }

  if (matched.length === 0) {
    return {
      content: input.content.trim(),
      targets: input.currentTargets,
      workerIds: input.currentWorkerIds,
      matched,
    };
  }
  return {
    content: remaining.trim(),
    targets: SELECTABLE_REPLY_TARGETS.filter((target) => targets.has(target)),
    workerIds: [...workerIds],
    matched,
  };
}
